/* eslint-disable react/forbid-prop-types */
/* eslint-disable react/jsx-props-no-spreading */
import React, { FunctionComponent, useMemo } from "react";
import * as Yup from "yup";
import { Formik, FormikProps } from "formik";
import { ScheduleConfigEventBlockType } from "@iventis/domain-model/model/scheduleConfigEventBlockType";
import { DataField } from "@iventis/domain-model/model/dataField";
import { DataFieldValue } from "@iventis/domain-model/model/dataFieldValue";
import { VenueLocation } from "@iventis/domain-model/model/venueLocation";
import { Hierarchy } from "@iventis/domain-model/model/hierarchy";
import { ScheduleVersion } from "@iventis/domain-model/model/scheduleVersion";
import { EventBlock } from "@iventis/domain-model/model/eventBlock";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { dataFieldIdToValueMapping, dataFieldToEmptyValueMapping } from "@iventis/utilities";
import { SubEventValues } from "./sub-event.types";
import { convertSubEventFormValuesToEventBlock, defaultInitialValues } from "./sub-events.helpers";
import { defaultSubEventValidations } from "./sub-event-validations";
import { SubEventForm } from "./sub-event-form";
import { createValidationForDataFields, getStartAndEndAtStringDigits } from "./schedule-helper-functions";
import { SportEventDataFieldNames } from "./datafields";
import { SubEventDialogMachineEvents } from "./sub-event-dialog.machine";

export enum SubEventCrudOperation {
    CREATE = "CREATE",
    EDIT = "EDIT",
}

export interface SubEventInitialValues {
    dataFields: DataFieldValue[];
    venueLocation: VenueLocation;
}

/** Sets the initial values and validation schema for formik to use on the sub event form */
export const SubEventFormWrapper: FunctionComponent<{
    existingEvent: Hierarchy<EventBlock>;
    session: Hierarchy<EventBlock>;
    formRef: React.MutableRefObject<FormikProps<SubEventValues>>;
    initialValueOverrides: Partial<SubEventValues>;
    allDataFields: DataField[];
    venueHierarchy: Hierarchy<VenueLocation>[];
    scheduleVersion: Hierarchy<ScheduleVersion>;
    eventBlockType: ScheduleConfigEventBlockType;
    eventBlockTypes: ScheduleConfigEventBlockType[];
    scheduleDaysMap: Map<number, Date>;
    currentDate: Date;
    send: (event: SubEventDialogMachineEvents | SubEventDialogMachineEvents[]) => void;
    timezone: string;
}> = ({
    session,
    scheduleVersion,
    eventBlockType,
    existingEvent,
    initialValueOverrides,
    allDataFields,
    venueHierarchy,
    eventBlockTypes,
    scheduleDaysMap,
    currentDate,
    formRef,
    send,
    timezone,
}) => {
    const translate = useIventisTranslate();

    // DATA FIELDS
    const dataFields = useMemo(() => allDataFields.filter(({ id }) => eventBlockType.dataFields.some((ebDf) => ebDf.dataFieldId === id)), []);
    const disciplineCodeDataFieldValue = useMemo(() => scheduleVersion.item.dataFieldValues.find((dfv) => dfv.dataField.name === SportEventDataFieldNames.DISCIPLINE), []);

    const initialFormValues = useMemo(() => {
        let values: SubEventValues;
        if (existingEvent) {
            values = {
                ...defaultInitialValues(timezone, { existingEvent, parentEvent: session }),
                dataFieldValues: dataFieldIdToValueMapping(existingEvent.item.dataFieldValues),
            } as SubEventValues;
        } else {
            values = {
                ...defaultInitialValues(timezone, { existingEvent, parentEvent: session }),
                dataFieldValues: dataFieldToEmptyValueMapping(dataFields),
                ...initialValueOverrides,
            } as SubEventValues;
        }

        dataFields.forEach((df) => {
            if (!Object.keys(values.dataFieldValues).includes(df.id)) {
                // Because controlled values cannot be undefined, we must explicitly set this missing data field value to null
                values.dataFieldValues[df.id] = null;
            }
        });

        return values;
    }, []);

    const validationSchema = useMemo(
        () =>
            Yup.object().shape({
                ...defaultSubEventValidations(translate, getStartAndEndAtStringDigits(session.item, timezone)),
                dataFieldValues: Yup.object(createValidationForDataFields(dataFields, eventBlockType.dataFields)),
            }),
        []
    );

    return (
        <Formik
            innerRef={formRef}
            initialValues={initialFormValues}
            validationSchema={validationSchema}
            onSubmit={async (values) =>
                send({
                    type: "SUBMIT",
                    payload: convertSubEventFormValuesToEventBlock(values, eventBlockType, dataFields, session.item.startAt, session.item, timezone, existingEvent?.item?.id),
                })
            }
            validateOnMount
        >
            {(formikProps) => (
                <SubEventForm
                    eventBlockType={eventBlockType}
                    venueHierarchy={venueHierarchy}
                    dataFields={allDataFields}
                    parentEvent={session}
                    disciplineCodeDataFieldValue={disciplineCodeDataFieldValue}
                    setCanSubmit={(isValid) => send({ type: isValid ? "VALID" : "INVALID" })}
                    eventBlockTypes={eventBlockTypes}
                    scheduleDaysMap={scheduleDaysMap}
                    currentDate={currentDate}
                    existingEvent={existingEvent?.item}
                    session={session.item}
                    timezone={timezone}
                    {...formikProps}
                />
            )}
        </Formik>
    );
};
